import type { PageAction, SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import type { ArgoCDGatewayResource, EnvironmentV2Resource, Repository } from "@octopusdeploy/octopus-server-client";
import { getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled, OctopusError, Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { useMemo, useState } from "react";
import { ActionChameleonAnchor } from "~/areas/ActionChameleonAnchor";
import type { ArgoCDInstancePageLoaderData } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstancePageLoaderData";
import { DeleteArgoCDInstanceDialog } from "~/areas/infrastructure/components/ArgoCDInstances/DeleteArgoCDInstanceDialog";
import { ConfigurationMessages } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/ConfigurationMessages";
import { GenerateArgoCDAnnotationsDrawer } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/GenerateArgoCDAnnotationsDrawer";
import { createGenerateAnnotationsPageAction } from "~/areas/infrastructure/components/ArgoCDInstances/createGenerateAnnotationsPageAction";
import { environmentV2ChipList } from "~/components/Chips/index";
import { EnvironmentV2MultiSelect } from "~/components/MultiSelect/EnvironmentV2MultiSelect";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { AllEnvironmentsTag } from "~/components/Tag";
import { ExpandableFormSection, required, Summary, Text } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
type ArgoCDInstanceSettingsPageProps = {
    loaderData: ArgoCDInstanceSettingsPageLoaderData;
    queryParams: ArgoCDInstanceSettingsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ArgoCDInstanceSettingsPageQueryParams>;
};
type ArgoCDInstanceSettingsPageQueryParams = {
    openAnnotationsDrawer: boolean | undefined;
};
export type ArgoCDInstanceSettingsPageLoaderData = ArgoCDInstancePageLoaderData & {
    environments: EnvironmentV2Resource[];
};
export async function argoCDInstanceSettingsPageLoader(repository: Repository, gatewayPromise: Promise<ArgoCDGatewayResource>, ephemeralEnvironmentsAreEnabled: boolean): Promise<ArgoCDInstanceSettingsPageLoaderData> {
    const gateway = await gatewayPromise;
    const environments = await getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, repository);
    return { gateway, environments };
}
function sanitiseGatewayToInstance(value: string): string {
    return value.replace(/([Aa]rgo\s*[Cc][Dd]\s*[Gg]ateway)/, "Argo CD instance");
}
export const ArgoCDInstanceSettingsPage = ({ loaderData, queryParams, setQueryParams }: ArgoCDInstanceSettingsPageProps) => {
    const { gateway, environments } = loaderData;
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const { model, setModel, formProps, createSaveAction, getFieldError } = useForm({
        formName: "ArgoCDInstance",
        initialModel: gateway,
        onSubmit: async (repository, model) => {
            try {
                return await repository.ArgoCDGateways.save({
                    SpaceId: model.SpaceId,
                    Id: model.Id,
                    Name: model.Name,
                    EnvironmentIds: model.EnvironmentIds,
                    WebUIUri: model.WebUIUri,
                });
            }
            catch (e) {
                //if this is an octopus error, it's probably a validation error, and we want to mutate the returned errors
                if (OctopusError.isOctopusError(e)) {
                    //we mutate the original error rather than create a new one
                    e.Errors = e.Errors ? e.Errors.map(sanitiseGatewayToInstance) : undefined;
                }
                throw e;
            }
        },
        updateFormModelAfterSubmit: (model) => model,
        submitPermission: { permission: Permission.MachineEdit },
    });
    const updateModel = (updated: Partial<ArgoCDGatewayResource>) => {
        setModel((prev) => ({ ...prev, ...updated }));
    };
    const nameSummary = model.Name ? Summary.summary(model.Name) : Summary.placeholder("Please enter a name");
    const webUIUriSummary = model.WebUIUri ? Summary.summary(model.WebUIUri) : Summary.placeholder("Please enter the URL to the Argo CD Web UI");
    const environmentsSummary = model.EnvironmentIds.length > 0 ? Summary.summary(environmentV2ChipList(environments, model.EnvironmentIds)) : Summary.summary(<AllEnvironmentsTag />);
    // in the UI we refer to gateways as instances, so sanitise any errors that use the term ArgoCDGateway
    const sanitizedGetFieldError = (fieldName: string) => {
        const fieldError = getFieldError(fieldName);
        return fieldError ? sanitiseGatewayToInstance(fieldError) : fieldError;
    };
    const additionalActions: PageAction[] = [createGenerateAnnotationsPageAction(() => setQueryParams({ ...queryParams, openAnnotationsDrawer: true }))];
    const overflowActions: SimpleMenuItem[] = useMemo(() => isAllowed({ permission: Permission.MachineDelete })
        ? [
            {
                type: "button",
                label: "Delete",
                onClick: () => setShowDeleteDialog(true),
            },
        ]
        : [], []);
    const coerceEmptyStringToNull = (val: string) => (StringHelper.isNullOrWhiteSpace(val) ? null : val);
    return (<PageContent header={{
            title: "Settings",
            primaryAction: createSaveAction(),
            pageActions: additionalActions,
            overflowActions,
        }} callout={{ type: "custom", content: <ActionChameleonAnchor actionTypes={["Octopus.ArgoCD.ImageUpdate"]}/> }}>
            <Form {...formProps}>
                <ExpandableFormSection errorKey="Name" title="Name" summary={nameSummary} help="A short, memorable, unique name for this Argo CD instance">
                    <Text value={model.Name} onChange={(Name) => updateModel({ Name })} error={sanitizedGetFieldError("Name")} validate={required(ConfigurationMessages.Name.Validation.NotProvided)} autoFocus={true} accessibleName={"gateway name"}/>
                </ExpandableFormSection>

                <ExpandableFormSection errorKey="WebUIUri" title="Web UI URL" summary={webUIUriSummary} help="The URL to the Argo CD Web UI">
                    <Text value={model.WebUIUri || ""} onChange={(WebUIUri) => updateModel({ WebUIUri: coerceEmptyStringToNull(WebUIUri) })} error={sanitizedGetFieldError("WebUIUri")} autoFocus={false} accessibleName="web ui uri" placeholder="Web UI URL"/>
                </ExpandableFormSection>

                <ExpandableFormSection errorKey="EnvironmentIds" title="Environments" summary={environmentsSummary} help={"Scope this Argo CD instance to specific environments."}>
                    <EnvironmentV2MultiSelect environments={environments} onChange={(EnvironmentIds) => updateModel({ EnvironmentIds })} value={model.EnvironmentIds} error={sanitizedGetFieldError("EnvironmentIds")} accessibleName={"gateway environments"}/>
                </ExpandableFormSection>
            </Form>

            <DeleteArgoCDInstanceDialog show={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} gateway={model}/>
            <GenerateArgoCDAnnotationsDrawer spaceId={loaderData.gateway.SpaceId} gatewayId={loaderData.gateway.Id} gatewayName={loaderData.gateway.Name} sourcePage="Settings" open={queryParams.openAnnotationsDrawer === true} onClose={() => setQueryParams({ ...queryParams, openAnnotationsDrawer: false })}/>
        </PageContent>);
};
